import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import userActions from './actions'
import { Grid, Button, Box, Input, Typography, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import PreviewTile from 'components/common/PreviewTile'
import PageHeader from 'components/common/PageHeader'
import { DOWNLOAD_USER_DATA, CREATE_USER, UPLOAD_USER_DATA } from 'api/UserConstants'
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AddIcon from '@mui/icons-material/Add';
import AppActions from 'components/app/actions'
import CreateUserDialog from './CreateUserDialog'
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });


const UsersOverview = () => {
    const { breakDown } = useSelector((state) => state.user)
    const [file, setFile] = useState(null);
    const { loadBreakDown, openCreateDialog, uploadUserData } = userActions()
    const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
    const [createdUsers, setCreatedUsers] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const { setToastMessage } = AppActions()

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0]; // Get the selected file

        if (!selectedFile) {
            alert("No file selected. Please choose a file.");
            return;
        }

        if (selectedFile.type !== "text/csv") {
            alert("Invalid file type. Please upload a CSV file.");
            return;
        }

        setFile(selectedFile);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };


    const handleUpload = () => {
        if (!file) {
            alert("Please select a file before uploading.");
            return;
        }
        uploadFile(file);
    };
    const uploadFile = async (file) => {
        if (!file) {
            alert("No file selected");
            return;
        }
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await fetch(UPLOAD_USER_DATA, {
                method: "POST",
                body: formData,
                credentials: "include",
                headers: {
                    "Accept": "*/*",
                },
            });

            const result = await response.json();
            setOpenDialog(true);

            if (response.ok) {
                setCreatedUsers(result.created_users || []);
                setValidationErrors(result.errors || []);
                // console.log("File uploaded successfully:", result);
            } else {
                setValidationErrors(result.errors || []);
                // console.error("Upload failed:", result.error || "Unknown error");
            }
        } catch (error) {
            console.error("An error occurred during file upload:", error);
        }
    };


    const handleCreateUserDialogClose = () => {
        setCreateUserDialogOpen(false);
    }

    const handleCreateUserDialogOpen = () => {
        setCreateUserDialogOpen(true);
    }

    const createUserFormSubmit = (user) => {
        fetch(CREATE_USER, {
            method: 'POST',
            body: JSON.stringify(user),
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(res => res.json())
            .then(data => {
                if (data.id) {
                    setToastMessage({
                        message: 'User created successfully!',
                        type: 'success'
                    })
                } else {
                    throw new Error(data.message)
                }
            })
            .catch(err => {
                console.log(err)
                setToastMessage({
                    message: err.message,
                    type: 'error'
                })
            })
            .finally(() => {
                handleCreateUserDialogClose();
            })
    }

    useEffect(() => {
        loadBreakDown()
    }, [loadBreakDown])

    const downloadUserData = () => {
        fetch(DOWNLOAD_USER_DATA, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(new Blob([blob]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'users.csv')
                document.body.appendChild(link)
                link.click()
                link.parentNode.removeChild(link)
            })
    }

    return (
        <>
            <PageHeader title="Users" subTitle="View and manage users">
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={downloadUserData}
                        startIcon={<DownloadIcon color="primary" />}
                    >
                        Download User Data
                    </Button>
                    <Button variant="contained" onClick={handleCreateUserDialogOpen} startIcon={<AddIcon />} style={{ marginLeft: '10px' }}>
                        Add User
                    </Button>
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Upload Results</DialogTitle>
                    <DialogContent>
                        <Typography variant="h6" style={{ color: 'green' }}>Created Users</Typography>
                        {createdUsers?.length > 0 ? (
                            <List>
                                {createdUsers?.map((user, index) => (
                                    <ListItem key={index}>
                                        <ListItemText primary={user} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography>No users created.</Typography>
                        )}
                        <Typography variant="h6" style={{ marginTop: '20px', color: 'red' }}>
                            Validation Errors
                        </Typography>
                        {validationErrors?.length > 0 ? (
                            <List>
                                {validationErrors?.map((error, index) => (
                                    <ListItem key={index}>
                                        <ListItemText
                                            primary={`Row ${error.row}: ${error.email || "Unknown email"}`}
                                            secondary={Object.values(error.errors || {}).flat().join(", ")}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography>No validation errors.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="primary" variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </PageHeader>
            <Grid container justify="flex-start" id="user-overview" spacing={2}>
                {[
                    {
                        h: 'Active Users',
                        c: breakDown && breakDown.members,
                        id: 'members',
                    },
                    // {
                    //     h: 'Admins',
                    //     c: breakDown && breakDown.admin_users,
                    //     id: 'super-user',
                    // },
                    {
                        h: 'Pending Approval',
                        c: breakDown && breakDown.pending_approval,
                        id: 'pending',
                    },
                    {
                        h: 'Inactive Users',
                        c: breakDown && breakDown.inactive_users,
                        id: 'inactive',
                    },
                ].map((value) => (
                    <PreviewTile
                        title={value.h}
                        text={String(value.c)}
                        key={value.h}
                        id={value.id}
                    />
                ))}
            </Grid>
            <CreateUserDialog
                open={createUserDialogOpen}
                handleClose={handleCreateUserDialogClose}
                handleFormSubmit={createUserFormSubmit}
            />
        </>
    )
}

export default UsersOverview
